/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import IconClose from '../Icons/IconClose';

const KEY_CODE_ESCAPE = 27;

// Content is visible as modal layer (former .isOpen)
const isOpenSx = {
  display: 'flex',
  flexDirection: 'column',
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: '100%',
  zIndex: 100,
};

// Content is explicitly hidden (former .isClosed)
const isClosedSx = {
  display: 'none',
};

// former .isOpen .scrollLayer (--marketplaceModalRootStyles + own declarations)
const scrollLayerBaseSx = (theme) => ({
  ...theme.mixins.marketplaceModalRootStyles,
  // Add default background color to avoid bouncing scroll showing the
  // page contents from behind the modal.
  backgroundColor: theme.tokens.matterColorLight,
  // Additional styles for the modal window, dimming the background and positioning the modal.
  // Two declarations reproduce the original CSS fallback: the second (mobile viewport bug fix)
  // only resolves on WebKit/Blink and is otherwise ignored, leaving 100vh.
  minHeight: '100vh',
  '@supports (min-height: -webkit-fill-available)': {
    minHeight: '-webkit-fill-available',
  },
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  // Merge the mixin's md block (justify/align) with the own md overrides;
  // both target @media (min-width:900px), so they must share one key (last value wins per prop).
  '@media (min-width:900px)': {
    ...theme.mixins.marketplaceModalRootStyles['@media (min-width:900px)'],
    padding: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    backgroundImage: 'none',
  },
});

// former .isOpen .container (--marketplaceModalBaseStyles + own declarations)
const containerBaseSx = (theme) => ({
  ...theme.mixins.marketplaceModalBaseStyles,
  minHeight: '100vh',
  '@supports (min-height: -webkit-fill-available)': {
    minHeight: '-webkit-fill-available',
  },
  height: '100%',
  // Merge the mixin's md block (padding/margins/border-bottom) with the own md overrides;
  // both target @media (min-width:900px), so they must share one key (last value wins per prop).
  '@media (min-width:900px)': {
    ...theme.mixins.marketplaceModalBaseStyles['@media (min-width:900px)'],
    flexBasis: '576px',
    minHeight: 'auto',
    height: 'auto',
  },
});

// former .content
const contentBaseSx = {
  paddingBottom: '120px',
  '@media (min-width:900px)': {
    paddingBottom: 0,
  },
};

// former .focusedDiv
const focusedDivSx = {
  '&:focus': {
    outline: 'none',
  },
};

const Portal = ({ portalRoot, children }) => {
  const elRef = useRef(null);
  if (elRef.current === null) {
    elRef.current = document.createElement('div');
  }

  useEffect(() => {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    const el = elRef.current;
    portalRoot.appendChild(el);
    return () => {
      portalRoot.removeChild(el);
    };
  }, [portalRoot]);

  return ReactDOM.createPortal(children, elRef.current);
};

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 * @param {string} [props.className]
 * @param {string} [props.scrollLayerClassName]
 * @param {React.ReactNode} [props.closeButtonMessage]
 * @param {string} [props.containerClassName]
 * @param {string} [props.contentClassName]
 * @param {Object} props.intl
 * @param {string} [props.isClosedClassName]
 * @param {boolean} [props.isOpen]
 * @param {boolean} [props.usePortal]
 * @param {React.ReactNode} [props.changeCloseButton]
 * @param {boolean} [props.lightCloseButton]
 * @param {string} props.id
 * @param {Function} [props.onClose]
 * @param {Function} props.onManageDisableScrolling
 * @param {Object | ((theme: Object) => Object)} [props.sx] - Additional sx merged onto the wrapper.
 * @param {Object | ((theme: Object) => Object)} [props.scrollLayerSx] - Additional sx merged onto the scroll layer.
 * @param {Object | ((theme: Object) => Object)} [props.containerSx] - sx for the container. When provided
 *   (and no containerClassName), it REPLACES the default container base styles (containerBaseSx), so a caller
 *   can supply a fully custom container style set; focusedDivSx (focus-outline reset) is still applied in the
 *   portal path. When containerClassName is set, containerSx is merged additively on top of it.
 * @param {Object | ((theme: Object) => Object)} [props.contentSx] - Additional sx for the content. When
 *   provided without contentClassName it REPLACES the default content base styles (contentBaseSx); when
 *   contentClassName is set, contentSx is merged additively on top.
 */
export const ModalComponent = ({
  children = null,
  className = null,
  scrollLayerClassName = null,
  closeButtonMessage = null,
  containerClassName = null,
  contentClassName = null,
  intl,
  // Intentionally NOT defaulted: the original `css.isClosed` base style applies ONLY when this
  // prop is omitted (undefined). Consumers (ModalInMobile) pass an explicit `null` on desktop to
  // opt out of any closed styling, which must remain distinct from "not passed".
  isClosedClassName,
  isOpen = false,
  usePortal = false,
  changeCloseButton = null,
  lightCloseButton = false,
  id,
  onClose = null,
  onManageDisableScrolling,
  sx = null,
  scrollLayerSx = null,
  containerSx = null,
  contentSx = null,
}) => {
  const refDiv = useRef(null);
  const [portalRoot, setPortalRoot] = useState(null);

  const handleClose = (event) => {
    onManageDisableScrolling(id, false);
    onClose(event);
  };

  // Keep latest values available to the body keyup listener without
  // re-subscribing on every render (matches the original single
  // mount/unmount listener lifecycle).
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // Mount: enable/disable scrolling, attach keyup listener, resolve portal root.
  // Unmount: detach listener, re-enable scrolling.
  useEffect(() => {
    onManageDisableScrolling(id, isOpen);

    const handleBodyKeyUp = (event) => {
      if (event.keyCode === KEY_CODE_ESCAPE && isOpenRef.current) {
        handleCloseRef.current(event);
      }
    };
    document.body.addEventListener('keyup', handleBodyKeyUp);

    setPortalRoot(document.getElementById('portal-root'));

    return () => {
      document.body.removeEventListener('keyup', handleBodyKeyUp);
      onManageDisableScrolling(id, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror componentDidUpdate: react to isOpen changes (skipping initial mount).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    onManageDisableScrolling(id, isOpen);

    // Because we are using portal,
    // we need to set the focus inside Modal manually.
    // refDiv is only attached in the portal branch below, and that branch renders
    // nothing until portalRoot has been resolved from the DOM. "No node to focus"
    // is therefore a state the component itself produces, not an anomaly — focusing
    // unconditionally throws and takes the whole page down via the error boundary.
    if (usePortal && isOpen && refDiv.current) {
      refDiv.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const closeBtn = isOpen ? (
    changeCloseButton ? (
      changeCloseButton
    ) : (
      <Button
        color="secondary"
        disableRipple
        endIcon={<IconClose color={lightCloseButton ? 'white' : null} />}
        onClick={handleClose}
        sx={(theme) => ({
          top: {
            xs: '10px',
            md: '20px',
          },
          right: {
            xs: '10px',
            md: '20px',
          },
          position: 'absolute',
          color: lightCloseButton ? theme.palette.common.white : theme.palette.text.primary,
        })}
        title={intl.formatMessage({ id: 'Modal.closeModal' })}
        variant="text"
      >
        {closeButtonMessage || <FormattedMessage id="Modal.close" />}
      </Button>
    )
  ) : null;

  // Modal uses given styles to wrap child components.
  // Reproduces classNames(modalClass, className) where modalClass = isOpen ? css.isOpen : isClosedClassName.
  // - open: base isOpen sx applies (className still appended).
  // - closed & isClosedClassName omitted (undefined): base closed sx applies (css.isClosed default).
  // - closed & isClosedClassName is a string: that override class applies, no base sx.
  // - closed & isClosedClassName === null: neither base sx nor closed class (just className).
  // This makes it possible to create ModalInMobile on top of Modal where style modes are:
  // visible, hidden, or none (ModalInMobile's children are always visible on desktop layout.)
  let wrapperSx;
  if (isOpen) {
    wrapperSx = isOpenSx;
  } else if (isClosedClassName === undefined) {
    wrapperSx = isClosedSx;
  } else {
    wrapperSx = undefined;
  }
  const wrapperClassName =
    (isOpen ? [className] : [isClosedClassName, className]).filter(Boolean).join(' ') || undefined;

  // If you want to use Portal https://reactjs.org/docs/portals.html
  // you need to use 'userPortal' flag.
  // ModalInMobile component needs to use the old Modal without the portal
  // because it's relying that the content is rendered inside
  // the DOM hierarchy of the parent component unlike Modal inside Portal.

  // The scroll-layer (backdrop dim + min-height + overflow) and container base styles were
  // originally scoped under `.isOpen .scrollLayer` / `.isOpen .container` in Modal.module.css,
  // so they applied ONLY when the modal was open. Gate them on `isOpen` here to preserve that:
  // ModalInMobile renders its content inline on desktop with isClosedClassName={null} (wrapper is
  // NOT display:none), so an unconditional scroll-layer leaked the dim backdrop and
  // `min-height:-webkit-fill-available` onto closed inline panels — visible as an empty grey map
  // box on WebKit (e.g. the Storyblok search-result panel when shouldShowSearchMap is false).
  const scrollLayerBaseMaybe = isOpen && !scrollLayerClassName ? scrollLayerBaseSx : undefined;
  const containerBaseMaybe = isOpen && !(containerClassName || containerSx) ? containerBaseSx : undefined;

  return !usePortal ? (
    <Box className={wrapperClassName} sx={[wrapperSx, sx].filter(Boolean)}>
      <Box className={scrollLayerClassName || undefined} sx={[scrollLayerBaseMaybe, scrollLayerSx].filter(Boolean)}>
        <Box className={containerClassName || undefined} sx={[containerBaseMaybe, containerSx].filter(Boolean)}>
          {closeBtn}
          <Box className={contentClassName || undefined} sx={[(contentClassName || contentSx) ? undefined : contentBaseSx, contentSx].filter(Boolean)}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  ) : portalRoot ? (
    <Portal portalRoot={portalRoot}>
      <Box className={wrapperClassName} sx={[wrapperSx, sx].filter(Boolean)}>
        <Box className={scrollLayerClassName || undefined} sx={[scrollLayerBaseMaybe, scrollLayerSx].filter(Boolean)}>
          <Box
            className={containerClassName || undefined}
            ref={refDiv}
            sx={[containerBaseMaybe ? (theme) => ({ ...containerBaseSx(theme), ...focusedDivSx }) : focusedDivSx, containerSx].filter(Boolean)}
            tabIndex="-1"
          >
            <Box className={contentClassName || undefined} sx={[(contentClassName || contentSx) ? undefined : contentBaseSx, contentSx].filter(Boolean)}>
              {children}
            </Box>
            {closeBtn}
          </Box>
        </Box>
      </Box>
    </Portal>
  ) : null;
};

export default injectIntl(ModalComponent);
